import type { CSSProperties } from 'react'
import { DEFAULT_DESIGN_CANVAS, type DesignCanvasSettings } from '../../types/design'

export type DesignCanvasPresetTone = 'Simple' | 'Premium' | 'Luxury'
export type DesignCanvasPattern = 'grid' | 'dot' | 'graph' | 'checker' | 'diagonal' | 'blueprint' | 'mesh' | 'ruler'

export type DesignCanvasPreset = {
  id: string
  name: string
  tone: DesignCanvasPresetTone
  description: string
  grid: number
  baseColor: string
  lineColor: string
  accentColor: string
  cssBackground: string
  pattern: DesignCanvasPattern
}

export const DESIGN_CANVAS_PRESETS: DesignCanvasPreset[] = [
  {
    id: 'graphite-grid',
    name: 'Graphite Grid',
    tone: 'Simple',
    description: 'Dark neutral grid for flowcharts, task maps, and daily planning.',
    grid: 24,
    baseColor: '#080b12', // mosaic-keep: user data
    lineColor: 'rgba(148, 163, 184, 0.16)', // mosaic-keep: user data
    accentColor: 'rgba(56, 189, 248, 0.28)', // mosaic-keep: user data
    pattern: 'grid',
    cssBackground: 'linear-gradient(#080b12, #0c111d)', // mosaic-keep: user data
  },
  {
    id: 'clean-paper',
    name: 'Clean Paper',
    tone: 'Simple',
    description: 'Plain light canvas for sketches, wireframes, and print-friendly diagrams.',
    grid: 24,
    baseColor: '#f8fafc', // mosaic-keep: user data
    lineColor: 'rgba(100, 116, 139, 0.18)', // mosaic-keep: user data
    accentColor: 'rgba(14, 165, 233, 0.22)', // mosaic-keep: user data
    pattern: 'grid',
    cssBackground: 'linear-gradient(#f8fafc, #eef2f7)', // mosaic-keep: user data
  },
  {
    id: 'dot-matrix',
    name: 'Dot Matrix',
    tone: 'Simple',
    description: 'Low-noise dot field for quick concept boards.',
    grid: 20,
    baseColor: '#0b1020', // mosaic-keep: user data
    lineColor: 'rgba(148, 163, 184, 0.26)', // mosaic-keep: user data
    accentColor: 'rgba(34, 211, 238, 0.34)', // mosaic-keep: user data
    pattern: 'dot',
    cssBackground: 'linear-gradient(135deg, #0b1020, #101827)', // mosaic-keep: user data
  },
  {
    id: 'soft-clay',
    name: 'Soft Clay',
    tone: 'Simple',
    description: 'Warm matte surface for friendly planning views.',
    grid: 28,
    baseColor: '#f3efe7', // mosaic-keep: user data
    lineColor: 'rgba(120, 113, 108, 0.18)', // mosaic-keep: user data
    accentColor: 'rgba(15, 118, 110, 0.2)', // mosaic-keep: user data
    pattern: 'graph',
    cssBackground: 'linear-gradient(135deg, #f3efe7, #e7ded0)', // mosaic-keep: user data
  },
  {
    id: 'transparent-check',
    name: 'Transparent Check',
    tone: 'Simple',
    description: 'Checkerboard-style artboard for assets with transparent edges.',
    grid: 20,
    baseColor: '#111827', // mosaic-keep: user data
    lineColor: 'rgba(203, 213, 225, 0.16)', // mosaic-keep: user data
    accentColor: 'rgba(148, 163, 184, 0.24)', // mosaic-keep: user data
    pattern: 'checker',
    cssBackground: 'linear-gradient(135deg, #111827, #172033)', // mosaic-keep: user data
  },
  {
    id: 'blueprint-studio',
    name: 'Blueprint Studio',
    tone: 'Premium',
    description: 'Architectural cyan grid for process maps and timeline structures.',
    grid: 32,
    baseColor: '#061426', // mosaic-keep: user data
    lineColor: 'rgba(125, 211, 252, 0.2)', // mosaic-keep: user data
    accentColor: 'rgba(56, 189, 248, 0.42)', // mosaic-keep: user data
    pattern: 'blueprint',
    cssBackground: 'linear-gradient(135deg, #061426, #09233f)', // mosaic-keep: user data
  },
  {
    id: 'midnight-command',
    name: 'Midnight Command',
    tone: 'Premium',
    description: 'High-contrast command center canvas for dashboards.',
    grid: 24,
    baseColor: '#030712', // mosaic-keep: user data
    lineColor: 'rgba(75, 85, 99, 0.36)', // mosaic-keep: user data
    accentColor: 'rgba(16, 185, 129, 0.32)', // mosaic-keep: user data
    pattern: 'mesh',
    cssBackground: 'linear-gradient(135deg, #030712, #101827 55%, #07111f)', // mosaic-keep: user data
  },
  {
    id: 'frosted-panel',
    name: 'Frosted Panel',
    tone: 'Premium',
    description: 'Cool glass-like drafting surface with a restrained grid.',
    grid: 24,
    baseColor: '#e7eef6', // mosaic-keep: user data
    lineColor: 'rgba(51, 65, 85, 0.18)', // mosaic-keep: user data
    accentColor: 'rgba(14, 165, 233, 0.24)', // mosaic-keep: user data
    pattern: 'graph',
    cssBackground: 'linear-gradient(135deg, #e7eef6, #cfddea)', // mosaic-keep: user data
  },
  {
    id: 'carbon-weave',
    name: 'Carbon Weave',
    tone: 'Premium',
    description: 'Technical woven texture for bold execution boards.',
    grid: 18,
    baseColor: '#09090b', // mosaic-keep: user data
    lineColor: 'rgba(161, 161, 170, 0.16)', // mosaic-keep: user data
    accentColor: 'rgba(244, 244, 245, 0.12)', // mosaic-keep: user data
    pattern: 'diagonal',
    cssBackground: 'linear-gradient(135deg, #09090b, #18181b)', // mosaic-keep: user data
  },
  {
    id: 'aurora-slab',
    name: 'Aurora Slab',
    tone: 'Premium',
    description: 'Soft color-shift slab for polished brainstorms.',
    grid: 28,
    baseColor: '#101827', // mosaic-keep: user data
    lineColor: 'rgba(165, 180, 252, 0.2)', // mosaic-keep: user data
    accentColor: 'rgba(45, 212, 191, 0.3)', // mosaic-keep: user data
    pattern: 'mesh',
    cssBackground: 'linear-gradient(120deg, #101827, #163044 48%, #251f43)', // mosaic-keep: user data
  },
  {
    id: 'platinum-mesh',
    name: 'Platinum Mesh',
    tone: 'Luxury',
    description: 'Bright premium mesh for board-room-ready diagrams.',
    grid: 30,
    baseColor: '#f1f5f9', // mosaic-keep: user data
    lineColor: 'rgba(71, 85, 105, 0.16)', // mosaic-keep: user data
    accentColor: 'rgba(180, 83, 9, 0.2)', // mosaic-keep: user data
    pattern: 'mesh',
    cssBackground: 'linear-gradient(135deg, #f8fafc, #dbe4ef 58%, #eef2f7)', // mosaic-keep: user data
  },
  {
    id: 'champagne-silk',
    name: 'Champagne Silk',
    tone: 'Luxury',
    description: 'Warm luxury canvas for premium timelines and proposals.',
    grid: 32,
    baseColor: '#f7edd7', // mosaic-keep: user data
    lineColor: 'rgba(146, 64, 14, 0.14)', // mosaic-keep: user data
    accentColor: 'rgba(202, 138, 4, 0.26)', // mosaic-keep: user data
    pattern: 'ruler',
    cssBackground: 'linear-gradient(135deg, #fff7e6, #ead3a7 52%, #f8edcf)', // mosaic-keep: user data
  },
  {
    id: 'obsidian-glass',
    name: 'Obsidian Glass',
    tone: 'Luxury',
    description: 'Glossy black canvas for high-end operational maps.',
    grid: 26,
    baseColor: '#020617', // mosaic-keep: user data
    lineColor: 'rgba(148, 163, 184, 0.14)', // mosaic-keep: user data
    accentColor: 'rgba(217, 119, 6, 0.32)', // mosaic-keep: user data
    pattern: 'mesh',
    cssBackground: 'linear-gradient(135deg, #020617, #111827 50%, #050505)', // mosaic-keep: user data
  },
  {
    id: 'emerald-noir',
    name: 'Emerald Noir',
    tone: 'Luxury',
    description: 'Dark green executive surface for strategy and finance views.',
    grid: 28,
    baseColor: '#031712', // mosaic-keep: user data
    lineColor: 'rgba(110, 231, 183, 0.16)', // mosaic-keep: user data
    accentColor: 'rgba(16, 185, 129, 0.34)', // mosaic-keep: user data
    pattern: 'mesh',
    cssBackground: 'linear-gradient(135deg, #031712, #064e3b 62%, #02120e)', // mosaic-keep: user data
  },
  {
    id: 'ruby-velvet',
    name: 'Ruby Velvet',
    tone: 'Luxury',
    description: 'Deep burgundy canvas for dramatic presentations.',
    grid: 28,
    baseColor: '#1f0712', // mosaic-keep: user data
    lineColor: 'rgba(251, 113, 133, 0.16)', // mosaic-keep: user data
    accentColor: 'rgba(244, 63, 94, 0.32)', // mosaic-keep: user data
    pattern: 'diagonal',
    cssBackground: 'linear-gradient(135deg, #1f0712, #4c0519 60%, #12040a)', // mosaic-keep: user data
  },
  {
    id: 'royal-marble',
    name: 'Royal Marble',
    tone: 'Luxury',
    description: 'Muted marble-like surface for premium diagrams.',
    grid: 34,
    baseColor: '#f4f1ec', // mosaic-keep: user data
    lineColor: 'rgba(71, 85, 105, 0.13)', // mosaic-keep: user data
    accentColor: 'rgba(15, 23, 42, 0.16)', // mosaic-keep: user data
    pattern: 'diagonal',
    cssBackground: 'linear-gradient(120deg, #f8fafc, #e7e1d6 35%, #f1f5f9 68%, #d8dee8)', // mosaic-keep: user data
  },
  {
    id: 'walnut-board',
    name: 'Walnut Board',
    tone: 'Luxury',
    description: 'Dark walnut-inspired drafting surface for classic premium work.',
    grid: 30,
    baseColor: '#1c1009', // mosaic-keep: user data
    lineColor: 'rgba(217, 119, 6, 0.12)', // mosaic-keep: user data
    accentColor: 'rgba(245, 158, 11, 0.2)', // mosaic-keep: user data
    pattern: 'ruler',
    cssBackground: 'linear-gradient(90deg, #1c1009, #2f1a0f 45%, #170b05)', // mosaic-keep: user data
  },
  {
    id: 'holographic-line',
    name: 'Holographic Line',
    tone: 'Premium',
    description: 'Prismatic dark canvas for futuristic product maps.',
    grid: 24,
    baseColor: '#08051a', // mosaic-keep: user data
    lineColor: 'rgba(192, 132, 252, 0.18)', // mosaic-keep: user data
    accentColor: 'rgba(34, 211, 238, 0.3)', // mosaic-keep: user data
    pattern: 'mesh',
    cssBackground: 'linear-gradient(125deg, #08051a, #10223a 42%, #27153f 72%, #06141f)', // mosaic-keep: user data
  },
  {
    id: 'ivory-atelier',
    name: 'Ivory Atelier',
    tone: 'Luxury',
    description: 'Quiet ivory surface for elegant planning and client-ready assets.',
    grid: 32,
    baseColor: '#fffaf0', // mosaic-keep: user data
    lineColor: 'rgba(100, 116, 139, 0.14)', // mosaic-keep: user data
    accentColor: 'rgba(217, 119, 6, 0.2)', // mosaic-keep: user data
    pattern: 'graph',
    cssBackground: 'linear-gradient(135deg, #fffaf0, #f1efe7)', // mosaic-keep: user data
  },
  {
    id: 'slate-ledger',
    name: 'Slate Ledger',
    tone: 'Simple',
    description: 'Quiet slate surface for dense work plans and simple timelines.',
    grid: 24,
    baseColor: '#111827', // mosaic-keep: user data
    lineColor: 'rgba(203, 213, 225, 0.12)', // mosaic-keep: user data
    accentColor: 'rgba(148, 163, 184, 0.22)', // mosaic-keep: user data
    pattern: 'graph',
    cssBackground: 'linear-gradient(135deg, #111827, #1f2937)', // mosaic-keep: user data
  },
]

export const resolveDesignCanvasPreset = (canvas?: DesignCanvasSettings | null): DesignCanvasPreset => {
  const id = canvas?.presetId ?? DEFAULT_DESIGN_CANVAS.presetId
  return DESIGN_CANVAS_PRESETS.find((preset) => preset.id === id) ?? DESIGN_CANVAS_PRESETS[0]
}

export const designCanvasStyle = (canvas?: DesignCanvasSettings | null): CSSProperties => {
  const preset = resolveDesignCanvasPreset(canvas)
  return {
    background: preset.cssBackground,
    ['--dh-canvas-base' as string]: preset.baseColor,
    ['--dh-canvas-line' as string]: preset.lineColor,
    ['--dh-canvas-accent' as string]: preset.accentColor,
  }
}

export const normalizeDesignCanvas = (canvas?: Partial<DesignCanvasSettings> | null): DesignCanvasSettings => ({
  ...DEFAULT_DESIGN_CANVAS,
  ...(canvas ?? {}),
  width: Number.isFinite(canvas?.width) ? Math.max(320, Number(canvas?.width)) : DEFAULT_DESIGN_CANVAS.width,
  height: Number.isFinite(canvas?.height) ? Math.max(240, Number(canvas?.height)) : DEFAULT_DESIGN_CANVAS.height,
  grid: Number.isFinite(canvas?.grid) ? Math.max(4, Number(canvas?.grid)) : resolveDesignCanvasPreset(canvas as DesignCanvasSettings | null).grid,
})

const patternId = (presetId: string) => `dh-canvas-pattern-${presetId}`

export const DesignCanvasBackdrop = ({
  canvas,
  x,
  y,
  width,
  height,
}: {
  canvas?: DesignCanvasSettings | null
  x: number
  y: number
  width: number
  height: number
}) => {
  const preset = resolveDesignCanvasPreset(canvas)
  const grid = canvas?.grid ?? preset.grid
  const id = patternId(preset.id)
  return (
    <>
      <defs>
        <pattern id={id} width={grid} height={grid} patternUnits="userSpaceOnUse">
          {preset.pattern === 'dot' && <circle cx={grid / 2} cy={grid / 2} r={1.35} fill={preset.lineColor} />}
          {preset.pattern === 'grid' && (
            <path d={`M ${grid} 0 L 0 0 0 ${grid}`} fill="none" stroke={preset.lineColor} strokeWidth="1" />
          )}
          {preset.pattern === 'graph' && (
            <>
              <path d={`M ${grid} 0 L 0 0 0 ${grid}`} fill="none" stroke={preset.lineColor} strokeWidth="1" />
              <path d={`M ${grid / 2} 0 L ${grid / 2} ${grid} M 0 ${grid / 2} L ${grid} ${grid / 2}`} fill="none" stroke={preset.lineColor} strokeWidth="0.5" opacity="0.55" />
            </>
          )}
          {preset.pattern === 'checker' && (
            <>
              <rect x="0" y="0" width={grid / 2} height={grid / 2} fill={preset.lineColor} opacity="0.52" />
              <rect x={grid / 2} y={grid / 2} width={grid / 2} height={grid / 2} fill={preset.lineColor} opacity="0.52" />
            </>
          )}
          {preset.pattern === 'diagonal' && (
            <>
              <path d={`M ${-grid / 2} ${grid} L ${grid} ${-grid / 2} M 0 ${grid * 1.5} L ${grid * 1.5} 0`} fill="none" stroke={preset.lineColor} strokeWidth="1" />
              <path d={`M 0 0 L ${grid} ${grid}`} fill="none" stroke={preset.accentColor} strokeWidth="0.6" opacity="0.45" />
            </>
          )}
          {preset.pattern === 'blueprint' && (
            <>
              <path d={`M ${grid} 0 L 0 0 0 ${grid}`} fill="none" stroke={preset.lineColor} strokeWidth="1" />
              <path d={`M ${grid / 4} 0 L ${grid / 4} ${grid} M ${grid / 2} 0 L ${grid / 2} ${grid} M ${grid * 0.75} 0 L ${grid * 0.75} ${grid} M 0 ${grid / 4} L ${grid} ${grid / 4} M 0 ${grid / 2} L ${grid} ${grid / 2} M 0 ${grid * 0.75} L ${grid} ${grid * 0.75}`} fill="none" stroke={preset.lineColor} strokeWidth="0.45" opacity="0.5" />
            </>
          )}
          {preset.pattern === 'mesh' && (
            <>
              <path d={`M ${grid} 0 L 0 0 0 ${grid}`} fill="none" stroke={preset.lineColor} strokeWidth="1" />
              <path d={`M 0 ${grid} L ${grid} 0`} fill="none" stroke={preset.accentColor} strokeWidth="0.65" opacity="0.42" />
            </>
          )}
          {preset.pattern === 'ruler' && (
            <>
              <path d={`M ${grid} 0 L 0 0 0 ${grid}`} fill="none" stroke={preset.lineColor} strokeWidth="1" />
              <path d={`M ${grid / 4} 0 L ${grid / 4} ${grid * 0.28} M ${grid / 2} 0 L ${grid / 2} ${grid * 0.42} M ${grid * 0.75} 0 L ${grid * 0.75} ${grid * 0.28} M 0 ${grid / 4} L ${grid * 0.28} ${grid / 4} M 0 ${grid / 2} L ${grid * 0.42} ${grid / 2} M 0 ${grid * 0.75} L ${grid * 0.28} ${grid * 0.75}`} fill="none" stroke={preset.accentColor} strokeWidth="0.8" opacity="0.55" />
            </>
          )}
        </pattern>
      </defs>
      <rect x={x} y={y} width={width} height={height} fill={preset.baseColor} pointerEvents="none" />
      <rect x={x} y={y} width={width} height={height} fill={`url(#${id})`} pointerEvents="none" />
    </>
  )
}
